import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, Grid3X3, ArrowLeftRight, Printer, LogOut, ShieldCheck, Eye, FileText, Settings, BookOpen } from "lucide-react";
import { LOGO } from "@/lib/kop";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "dashboard", color: "#14b8a6" },
  { to: "/warga", label: "Data Warga", icon: Users, id: "warga", color: "#3b82f6" },
  { to: "/rekap", label: "Rekap per RT", icon: Grid3X3, id: "rekap", color: "#8b5cf6" },
  { to: "/mutasi", label: "Mutasi Warga", icon: ArrowLeftRight, id: "mutasi", color: "#f59e0b" },
  { to: "/surat", label: "Layanan Surat", icon: FileText, id: "surat", color: "#10b981" },
  { to: "/agenda", label: "Buku Agenda", icon: BookOpen, id: "agenda", color: "#f43f5e" },
  { to: "/pengaturan", label: "Pengaturan Pejabat", icon: Settings, id: "pengaturan", color: "#94a3b8" },
];
const accentFor = (path) => (NAV.filter((n) => n.to !== "/").find((n) => path.startsWith(n.to)) || NAV[0]).color;

const linkCls = ({ isActive }) =>
  `group relative flex items-center gap-3 pl-3 pr-3 py-2 rounded-xl text-[13.5px] font-medium transition-all duration-200 whitespace-nowrap ${
    isActive ? "bg-white/[0.12] text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]" : "text-slate-300 hover:bg-white/[0.06] hover:text-white"
  }`;

const NavIcon = ({ icon: Icon, color }) => (
  <span className="h-8 w-8 rounded-lg grid place-items-center transition-transform duration-200 group-hover:scale-105" style={{ background: `${color}26`, color }}>
    <Icon className="h-4 w-4" />
  </span>
);

export default function Layout() {
  const { user, logout, isAdmin } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const accent = accentFor(pathname);
  const doLogout = async () => {
    await logout();
    nav("/login");
  };
  return (
    <div className="min-h-screen md:flex">
      <aside className="no-print md:w-[268px] md:min-h-screen bg-gradient-to-b from-[#0b1f33] via-[#0c2a3d] to-[#0b3b3a] text-white md:sticky md:top-0 md:h-screen flex flex-col">
        <div className="papua-trim" />
        <div className="px-5 py-5 flex items-center gap-3">
          <img src={LOGO} alt="Logo Kabupaten Mimika" className="h-12 w-auto drop-shadow" data-testid="sidebar-logo" />
          <div>
            <div className="font-display font-extrabold tracking-tight leading-none" data-testid="app-name">TAPARU INAUGA</div>
            <div className="text-[11px] text-emerald-100/60 mt-1">Kelurahan Inauga · Distrik Wania</div>
          </div>
        </div>
        <p className="hidden md:block px-5 -mt-2 mb-4 text-[11px] leading-snug text-amber-300/80 italic" data-testid="app-tagline">Tata Administrasi Penduduk Akurat & Rekapitulasi Umum Inauga</p>
        <nav className="px-3 flex md:flex-col gap-1 overflow-x-auto md:overflow-y-auto pb-3 md:pb-0">
          <div className="hidden md:block px-3 pt-1 pb-2 text-[10px] font-semibold tracking-[0.2em] text-slate-400 uppercase">Menu Utama</div>
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} className={linkCls} data-testid={`nav-${n.id}`}>
              {({ isActive }) => (
                <>
                  {isActive && <span className="absolute left-0 top-2 bottom-2 w-1 rounded-r" style={{ background: n.color }} />}
                  <NavIcon icon={n.icon} color={n.color} /> {n.label}
                </>
              )}
            </NavLink>
          ))}
          <a href="/cetak" target="_blank" rel="noreferrer" className={linkCls({ isActive: false })} data-testid="nav-cetak">
            <NavIcon icon={Printer} color="#fbbf24" /> Cetak Rekap
          </a>
        </nav>
        <div className="hidden md:block mt-auto p-4 border-t border-white/10">
          <UserBox user={user} isAdmin={isAdmin} onLogout={doLogout} />
        </div>
      </aside>
      <main className="app-main flex-1 min-w-0" style={{ "--accent": accent }} data-testid="app-main">
        <div className="md:hidden no-print px-4 py-3 bg-white border-b">
          <UserBox user={user} isAdmin={isAdmin} onLogout={doLogout} compact />
        </div>
        <div className="p-4 sm:p-6 lg:p-8 max-w-[1500px]">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

const UserBox = ({ user, isAdmin, onLogout, compact }) => (
  <div className={`flex items-center gap-3 ${compact ? "justify-between" : "flex-col items-start"}`}>
    <div>
      <div className={`text-sm font-semibold ${compact ? "text-slate-900" : "text-white"}`} data-testid="current-user-name">{user?.name}</div>
      <span
        data-testid="current-user-role"
        className={`inline-flex items-center gap-1 mt-1 text-[11px] px-2 py-0.5 rounded-full border ${
          isAdmin ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-600 md:text-emerald-300" : "bg-amber-500/15 border-amber-400/40 text-amber-700 md:text-amber-300"
        }`}
      >
        {isAdmin ? <ShieldCheck className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
        {isAdmin ? "Admin Kelurahan" : "Lurah · Mode Lihat"}
      </span>
    </div>
    <button
      onClick={onLogout}
      data-testid="logout-button"
      className={`inline-flex items-center gap-2 text-sm rounded-lg px-3 py-2 transition-colors ${
        compact ? "border text-slate-700 hover:bg-slate-50" : "w-full bg-white/10 hover:bg-white/20 text-white"
      }`}
    >
      <LogOut className="h-4 w-4" /> Keluar
    </button>
  </div>
);
